import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import knowledgeApi from '@/lib/knowledge-api'
import type { ReconciliationRun } from '@/types/reconciliation'

// ── Trigger a new reconciliation run ──
export function useReconciliationRun(kbId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const res = await knowledgeApi.post(`/knowledge/${kbId}/taxonomy/reconciliation/run/`)
      return res.data as ReconciliationRun
    },
    onSuccess: () => {
      
      queryClient.invalidateQueries({ queryKey: ['reconciliation-latest', kbId] })
      queryClient.invalidateQueries({ queryKey: ['reconciliation-status', kbId] })
    },
  })
}

// ── Get latest reconciliation for this KB ──
export function useLatestReconciliation(kbId: string) {
  return useQuery<ReconciliationRun | null>({
    queryKey: ['reconciliation-latest', kbId],
    queryFn: async () => {
      try {
        const res = await knowledgeApi.get(`/knowledge/${kbId}/taxonomy/reconciliation/latest/`)
        return res.data as ReconciliationRun
      } catch (err: any) {
        if (err?.response?.status === 404) return null
        throw err
      }
    },
    enabled: !!kbId,
    staleTime: 5 * 60 * 1000,
  })
}

// ── Poll reconciliation status and results ──
// Polls every 3s while processing, stops on terminal state or after 5 min timeout
const POLL_INTERVAL = 3000
const POLL_TIMEOUT_MS = 5 * 60 * 1000

export function useReconciliationStatus(kbId: string, reconciliationId: string | null) {
  return useQuery<ReconciliationRun | null>({
    queryKey: ['reconciliation-status', kbId, reconciliationId],
    queryFn: async () => {
      const res = await knowledgeApi.get(
        `/knowledge/${kbId}/taxonomy/reconciliation/${reconciliationId}/`
      )
      return res.data as ReconciliationRun
    },
    enabled: !!kbId && !!reconciliationId,
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data) return false
      if (data.status !== 'processing') return false

      // Safety timeout: stop polling if created > 5 min ago
      if (data.created_at) {
        const age = Date.now() - new Date(data.created_at).getTime()
        if (age > POLL_TIMEOUT_MS) return false
      }

      return POLL_INTERVAL
    },
    refetchIntervalInBackground: true,
  })
}

// ── Approve or reject a merge group ──
export function useUpdateMergeDecision(kbId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ groupId, userApproved }: { groupId: string; userApproved: boolean }) => {
      const res = await knowledgeApi.patch(
        `/knowledge/${kbId}/taxonomy/reconciliation/merge-groups/${groupId}/`,
        { user_approved: userApproved }
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reconciliation-status', kbId] })
      queryClient.invalidateQueries({ queryKey: ['reconciliation-latest', kbId] })
    },
  })
}

// ── Finalize reconciliation ──
export function useFinalizeReconciliation(kbId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (reconciliationId: string) => {
      const res = await knowledgeApi.post(
        `/knowledge/${kbId}/taxonomy/reconciliation/${reconciliationId}/finalize/`
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reconciliation-status', kbId] })
      queryClient.invalidateQueries({ queryKey: ['reconciliation-latest', kbId] })
    },
  })
}
